import type * as Api from '@ViewModels';
import * as React from 'react';

const STORAGE_KEY = 'ai-coaching-review:rep-cards';

const readStoredReps = (): Api.IUser[] => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return [];
		}
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed)
			? parsed.filter((user): user is Api.IUser => Boolean(user) && typeof user.id === 'string')
			: [];
	} catch {
		return [];
	}
};

/** Caches whole `IUser` objects; a stale cached name self-corrects because each card re-resolves by id. */
export function usePersistedRepCards() {
	const [reps, setRepsState] = React.useState<Api.IUser[]>(readStoredReps);

	const setReps = React.useCallback((action: React.SetStateAction<Api.IUser[]>) => {
		setRepsState(prev => {
			const next = typeof action === 'function' ? (action as (p: Api.IUser[]) => Api.IUser[])(prev) : action;
			try {
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
			} catch {
				// Ignore write failures (private mode / storage disabled) — in-memory state still updates.
			}
			return next;
		});
	}, []);

	return [reps, setReps] as const;
}
